import React from 'react';
import { Card, Progress, List, Tag } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled } from '@ant-design/icons';

const SeoScore = ({ title = '', content = '', slug = '', keyword = '', titleSeo = '', descriptionSeo = '' }) => {
  const calculateScore = () => {
    let score = 0;
    const checks = [];
    const textContent = content.replace(/<[^>]*>/g, ' ');
    const wordCount = textContent.trim().split(/\s+/).filter(w => w.length > 0).length;

    const finalTitle = titleSeo || title;
    const finalDesc = descriptionSeo || textContent.substring(0, 160);

    // 1. Title Length (Max 10 points)
    const titleLen = finalTitle?.length || 0;
    if (titleLen >= 50 && titleLen <= 60) {
      score += 10;
      checks.push({ status: 'success', text: 'Tiêu đề có độ dài tối ưu (50-60 ký tự).' });
    } else if (titleLen > 0) {
      score += 5;
      checks.push({ status: 'warning', text: 'Tiêu đề nên từ 50-60 ký tự.' });
    } else {
      checks.push({ status: 'error', text: 'Thiếu tiêu đề bài viết.' });
    }

    // 2. Keyword in Title (Max 10 points)
    if (keyword && finalTitle.toLowerCase().includes(keyword.toLowerCase())) {
      score += 10;
      checks.push({ status: 'success', text: 'Từ khóa chính xuất hiện trong tiêu đề SEO.' });
    } else if (keyword) {
      checks.push({ status: 'warning', text: 'Nên đưa từ khóa chính vào tiêu đề SEO.' });
    }

    // 3. Slug Optimization (Max 10 points)
    if (slug) {
      const slugCorrect = /^[a-z0-9-]+$/.test(slug);
      const containsKeyword = keyword ? slug.includes(keyword.toLowerCase().replace(/\s+/g, '-')) : true;
      if (slugCorrect && containsKeyword) {
        score += 10;
        checks.push({ status: 'success', text: 'Đường dẫn (Slug) chuẩn SEO.' });
      } else {
        checks.push({ status: 'warning', text: 'Đường dẫn nên chứa từ khóa và không có mã đặc biệt.' });
      }
    }

    // 4. Content Length (Max 15 points)
    if (wordCount >= 600) {
      score += 15;
      checks.push({ status: 'success', text: `Nội dung chuyên sâu (${wordCount} từ).` });
    } else if (wordCount >= 300) {
      score += 10;
      checks.push({ status: 'warning', text: `Nội dung khá (${wordCount} từ), nên viết thêm.` });
    } else {
      checks.push({ status: 'error', text: 'Nội dung quá ngắn (ít hơn 300 từ).' });
    }

    // 5. Keyword in Content (Max 10 points)
    const keywordRegex = keyword ? new RegExp(keyword, 'gi') : null;
    const keywordMatches = keyword && keywordRegex ? (textContent.match(keywordRegex) || []).length : 0;
    const density = wordCount > 0 ? (keywordMatches / wordCount) * 100 : 0;

    if (keyword && keywordMatches > 0) {
      if (density >= 0.5 && density <= 2.5) {
        score += 10;
        checks.push({ status: 'success', text: `Mật độ từ khóa tốt (${density.toFixed(1)}%).` });
      } else if (density > 2.5) {
        score += 5;
        checks.push({ status: 'warning', text: 'Mật độ từ khóa hơi cao, tránh nhồi nhét.' });
      } else {
        score += 5;
        checks.push({ status: 'warning', text: 'Nên lặp lại từ khóa chính vài lần nữa.' });
      }
    }

    // 6. Keyword in First Paragraph (Max 10 points)
    const firstParagraph = textContent.substring(0, 500).toLowerCase();
    if (keyword && firstParagraph.includes(keyword.toLowerCase())) {
      score += 10;
      checks.push({ status: 'success', text: 'Từ khóa xuất hiện trong đoạn đầu tiên.' });
    } else if (keyword) {
      checks.push({ status: 'warning', text: 'Từ khóa chính nên xuất hiện ngay đoạn đầu.' });
    }

    // 7. Headings Check (Max 10 points)
    const hasH2 = content.includes('<h2') || content.includes('<h3');
    if (hasH2) {
      score += 10;
      checks.push({ status: 'success', text: 'Đã sử dụng các thẻ tiêu đề phụ (H2, H3).' });
    } else {
      checks.push({ status: 'warning', text: 'Nên sử dụng H2, H3 để phân tách nội dung.' });
    }

    // 8. Meta Description Check (Max 10 points)
    const descLen = finalDesc?.length || 0;
    if (descLen >= 120 && descLen <= 160) {
      score += 10;
      checks.push({ status: 'success', text: 'Mô tả SEO có độ dài tối ưu (120-160 ký tự).' });
    } else if (descLen > 0) {
      score += 5;
      checks.push({ status: 'warning', text: 'Mô tả SEO nên từ 120-160 ký tự.' });
    }

    // 9. Images Check (Max 10 points)
    if (content.includes('<img')) {
      score += 10;
      checks.push({ status: 'success', text: 'Nội dung có hình ảnh minh họa.' });
    } else {
      checks.push({ status: 'warning', text: 'Nên thêm hình ảnh để bài viết sinh động hơn.' });
    }

    // 10. Links Check (Max 5 points)
    if (content.includes('<a ')) {
      score += 5;
      checks.push({ status: 'success', text: 'Đã có liên kết (Internal/External links).' });
    } else {
      checks.push({ status: 'warning', text: 'Nên thêm liên kết để tăng tính kết nối.' });
    }

    return { score: Math.min(score, 100), checks };
  };

  const { score, checks } = calculateScore();

  const getStatusColor = (s) => {
    if (s >= 80) return '#52c41a';
    if (s >= 50) return '#faad14';
    return '#f5222d';
  };

  return (
    <Card
      title={<span className="font-bold text-lg">Phân tích SEO chuyên sâu</span>}
      className="shadow-md border-gray-100 sticky top-4 rounded-2xl overflow-hidden"
    >
      <div className="text-center mb-6">
        <Progress
          type="dashboard"
          percent={score}
          strokeColor={getStatusColor(score)}
          strokeWidth={10}
          format={(percent) => (
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black" style={{ color: getStatusColor(score) }}>{percent}</span>
              <span className="text-[10px] uppercase text-gray-400 font-bold tracking-widest mt-1">SEO Score</span>
            </div>
          )}
        />
      </div>

      <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
        <List
          size="small"
          dataSource={checks}
          renderItem={(item) => (
            <List.Item className="border-none px-0 py-2">
              <div className="flex gap-3 items-start">
                <div className="mt-1">
                  {item.status === 'success' && <CheckCircleFilled className="text-green-500 text-base" />}
                  {item.status === 'warning' && <InfoCircleFilled className="text-yellow-500 text-base" />}
                  {item.status === 'error' && <CloseCircleFilled className="text-red-500 text-base" />}
                </div>
                <span className={`text-[13px] leading-relaxed ${item.status === 'error' ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                  {item.text}
                </span>
              </div>
            </List.Item>
          )}
        />
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex justify-between items-center bg-gray-50 p-3 rounded-xl">
          <span className="text-xs font-bold text-gray-500">Đánh giá chung:</span>
          {score >= 80 ? <Tag color="green" className="font-bold border-none m-0">Tuyệt vời</Tag> :
            score >= 50 ? <Tag color="orange" className="font-bold border-none m-0">Cần cải thiện</Tag> :
              <Tag color="red" className="font-bold border-none m-0">Kém</Tag>}
        </div>
      </div>
    </Card>
  );
};

export default SeoScore;
